import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { rupiah, todayISO, toDateInput } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PackageCheck } from "lucide-react";
import { rit } from "@/components/app/bakar/StokKayuBar";

export const BongkarDialog = ({ open, setOpen, item, onSaved }) => {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (!open || !item) return;
    const selesai = item.status === "selesai";
    setForm({
      tanggal_bongkar: selesai ? toDateInput(item.tanggal_bongkar) : todayISO(),
      output_kg: selesai ? item.output_kg : "",
      kulitan_kg: selesai ? (item.kulitan_kg || 0) : "",
      biaya_operasional: selesai ? item.biaya_operasional : 0,
      keterangan: item.keterangan_bongkar || "",
    });
  }, [open, item]);
  if (!item) return null;

  const kg = Number(form.output_kg || 0);
  const kul = Number(form.kulitan_kg || 0);
  const total = Number(item.biaya_kayu || 0) + Number(form.biaya_operasional || 0);
  const hpp = kg > 0 ? Math.round(total / kg) : 0;
  const rendemen = kg + kul > 0 && item.jumlah_rit > 0 ? Math.round((kg + kul) / item.jumlah_rit) : 0;
  const upd = (k) => (e) => setForm({ ...form, [k]: e && e.target ? e.target.value : e });

  const save = async (e) => {
    e?.preventDefault();
    if (!form.tanggal_bongkar) { toast.error("Tanggal bongkar wajib diisi"); return; }
    if (!(kg > 0)) { toast.error("Hasil Arang (Kg) wajib diisi (lebih dari 0)"); return; }
    try {
      await api.post(`/batch-bakar/${item.id}/bongkar`, {
        tanggal_bongkar: form.tanggal_bongkar, output_kg: kg, kulitan_kg: kul,
        biaya_operasional: Number(form.biaya_operasional || 0), keterangan: form.keterangan || "",
      });
      toast.success("Hasil bongkar tersimpan & stok arang bertambah");
      setOpen(false); onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="bongkar-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><PackageCheck size={18} className="text-emerald-600"/>Bongkar Tungku — {item.produk_nama}</DialogTitle></DialogHeader>
        <div className="text-xs bg-slate-50 border border-slate-200 rounded-sm p-2 text-slate-700" data-testid="bongkar-info">
          Kayu: <b>{rit(item.jumlah_rit)}</b> · Biaya kayu: <b>{rupiah(item.biaya_kayu)}</b>
        </div>
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Tanggal Bongkar <span className="text-rose-500">*</span></Label><Input type="date" value={form.tanggal_bongkar || ""} onChange={upd("tanggal_bongkar")} data-testid="bongkar-tanggal"/></div>
          <div className="space-y-1.5"><Label>Hasil {item.produk_nama} (Kg) <span className="text-rose-500">*</span></Label><Input type="number" min="0" step="0.01" value={form.output_kg} onChange={upd("output_kg")} data-testid="bongkar-kg"/></div>
          <div className="md:col-span-2 space-y-1.5"><Label>Hasil Kulitan/Remuk (Kg)</Label>
            <Input type="number" min="0" step="0.01" value={form.kulitan_kg} onChange={upd("kulitan_kg")} placeholder="0" data-testid="bongkar-kulitan"/>
            <div className="text-[11px] text-slate-500">Bonus: masuk stok Kulitan dengan HPP Rp0 (harga jual bebas). Bisa dijual ke luar atau ke Pabrik Briket.</div>
          </div>
          <div className="md:col-span-2 space-y-1.5"><Label>Biaya Operasional Selama Bakar (Rp)</Label>
            <CurrencyInput value={form.biaya_operasional} onValueChange={upd("biaya_operasional")} data-testid="bongkar-biaya-op"/>
            <div className="text-[11px] text-slate-500">Upah tukang bakar/bongkar, solar, karung, dll.</div>
          </div>
          <div className="space-y-1.5"><Label>HPP / Kg Arang Tungku</Label><div className="h-9 px-3 flex items-center border border-slate-200 bg-blue-50 font-mono font-semibold brand-text" data-testid="bongkar-hpp">{rupiah(hpp)} / Kg</div></div>
          <div className="space-y-1.5"><Label>Rendemen</Label><div className="h-9 px-3 flex items-center border border-slate-200 bg-slate-50 font-mono font-semibold" data-testid="bongkar-rendemen">{rendemen.toLocaleString("id-ID")} Kg/rit</div></div>
          <div className="md:col-span-2 space-y-1.5"><Label>Keterangan</Label><Textarea value={form.keterangan || ""} onChange={upd("keterangan")} data-testid="bongkar-keterangan"/></div>
          <button type="submit" className="hidden" aria-hidden="true"/>
        </form>
        <div className="text-[11px] text-slate-500 bg-amber-50 border border-amber-200 rounded-sm p-2">
          Hasil otomatis masuk ke Riwayat Stok Masuk (stok & HPP arang; kulitan HPP Rp0). Biaya operasional otomatis dicatat di Pengeluaran.
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Batal</Button>
          <Button onClick={save} className="brand-btn rounded-sm" data-testid="bongkar-save">Simpan Hasil</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
